import { WorkPage, WorkSection, WorkEmpty } from '../components/work-page.tsx';
import { LiveMapLink } from './live-map.tsx';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { customerApi, reengagementApi } from '../api/endpoints.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, LoadingBlock, Pagination, SplitButton, TextInput } from '../components/ui.tsx';
import { FounderContactLine } from '../components/founder-contact.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import CustomerForm from '../forms/customer-form.tsx';
import { UnknownSendersInbox } from '../components/conversation-timeline.tsx';

/**
 * The list as a file. A plain navigation rather than a fetch: the session
 * cookie authenticates it and `content-disposition` names it, exactly as the
 * report downloads work.
 */
function downloadExport(format: 'csv' | 'xlsx'): void {
  window.location.assign(customerApi.exportUrl(format));
}

export default function CustomersPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('customers.list.documentTitle'));
  const { can } = useSession();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);

  const followUps = useQuery({
    queryKey: ['reengagement', 'summary'],
    queryFn: () => reengagementApi.summary(),
    enabled: can('customers:read'),
    // This count is an action prompt, not historical reporting. Never replace
    // a failed network read with a persisted number another device may have
    // already worked down.
    networkMode: 'always',
    staleTime: 0,
  });

  const query = useQuery({
    queryKey: ['customers', { search, page }],
    queryFn: () => customerApi.list({ q: search, page, pageSize: 25 }),
    // Keeps the previous page on screen while the next one loads, so the
    // pager neither disappears nor forgets how many pages there are mid-click.
    placeholderData: keepPreviousData,
  });

  return (
    <WorkPage
      title={t('customers.list.title')}
      description={t('customers.list.description')}
      actions={
        <>
          {can('customers:read') ? (
            <Link className="btn btn-secondary" to="/customers/follow-ups">
              {t('customers.list.followUps')}
              {followUps.data ? (
                <span
                  className="ml-1 rounded-full bg-[color:var(--color-brand-soft)] px-2 py-0.5 text-xs font-semibold"
                  aria-label={t('customers.list.followUpsCount', { count: followUps.data.open })}
                >
                  {followUps.data.open}
                </span>
              ) : null}
            </Link>
          ) : null}
          {can('customers:read') ? (
            <div className="customer-work-export">
              <SplitButton
                label={t('customers.list.exportCsv')}
                menuLabel={t('customers.list.exportMenuLabel')}
                variant="secondary"
                onClick={() => downloadExport('csv')}
                actions={[
                  {
                    key: 'xlsx',
                    label: t('customers.list.exportXlsx'),
                    hint: t('customers.list.exportXlsxHint'),
                    onSelect: () => downloadExport('xlsx'),
                  },
                ]}
              />
            </div>
          ) : null}
          {can('customers:write') ? (
            <Link className="btn btn-secondary" to="/customers/import">
              {t('customers.list.importCustomers')}
            </Link>
          ) : null}
          {can('customers:write') && !adding ? (
            <Button variant="primary" onClick={() => setAdding(true)}>
              {t('customers.list.addCustomer')}
            </Button>
          ) : null}
        </>
      }
    >
      <LiveMapLink customers />

      {adding ? (
        <WorkSection title={t('customers.list.addCustomer')}>
          <CustomerForm onSaved={() => setAdding(false)} onCancel={() => setAdding(false)} />
        </WorkSection>
      ) : null}

      {/* Texts from numbers nobody recognized, above the list rather than
          behind a tab: an unanswered stranger is a prospective customer, and
          this is the page somebody is on when they decide who they are. Renders
          nothing at all when there are none. */}
      {can('customers:write') ? <UnknownSendersInbox /> : null}

      <div className="card mb-6">
        <TextInput
          label={t('customers.list.searchLabel')}
          type="search"
          placeholder={t('customers.list.searchPlaceholder')}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
        />
      </div>

      <WorkSection title={t('customers.list.title')}>
        {query.isLoading ? <LoadingBlock label={t('customers.list.loading')} /> : null}

        {query.isError ? (
          <Alert tone="error" title={t('customers.list.loadErrorTitle')}>
            <p>{(query.error as Error).message}</p>
            <Button className="mt-3" busy={query.isFetching} onClick={() => void query.refetch()}>
              {t('common.retry')}
            </Button>
          </Alert>
        ) : null}

        {!query.isError && !query.isPlaceholderData && query.data && query.data.items.length === 0 ? (
          <WorkEmpty title={t(search.trim() ? 'customers.list.emptyFilteredTitle' : 'customers.list.emptyTitle')}>
            <p>{search.trim() ? t('customers.list.emptyFiltered') : t('customers.list.emptyDefault')}</p>
            {search.trim() ? (
              <Button
                className="mt-3"
                onClick={() => {
                  setSearch('');
                  setPage(1);
                }}
              >
                {t('customers.list.clearSearch')}
              </Button>
            ) : can('customers:write') && !adding ? (
              <Button className="mt-3" variant="primary" onClick={() => setAdding(true)}>
                {t('customers.list.addCustomer')}
              </Button>
            ) : null}
            {search ? null : <FounderContactLine className="mt-3 text-sm text-[color:var(--color-muted)]" />}
          </WorkEmpty>
        ) : null}

        {query.data && query.data.items.length > 0 ? (
          <ul className="divide-y divide-[color:var(--color-line)] rounded-lg border border-[color:var(--color-line)] bg-white">
            {query.data.items.map((customer) => (
              <li key={customer.id}>
                <Link
                  to={`/customers/${customer.id}`}
                  className="block px-4 py-3 hover:bg-[color:var(--color-surface)]"
                >
                  <span className="font-semibold text-[color:var(--color-brand)]">{customer.displayName}</span>
                  <span className="block text-sm text-[color:var(--color-muted)]">
                    {[customer.email, customer.phone].filter(Boolean).join(' · ') ||
                      t('customers.list.noContactDetails')}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : null}

        <Pagination page={page} totalPages={query.data?.totalPages} onPageChange={setPage} />
      </WorkSection>
    </WorkPage>
  );
}
